// ContactService: Handle contact form submissions
// This service can be extended to integrate with email services like SendGrid, Resend, etc.

import { supabase } from '@/config/supabase'

export interface ContactFormData {
  email: string
  category: string
  subject: string
  message: string
}

export interface ContactSubmissionResult {
  success: boolean
  error?: string
}

class ContactService {
  /**
   * Submit a contact form
   * For now, this stores in Supabase and can be extended with email sending
   */
  async submitContactForm(formData: ContactFormData): Promise<ContactSubmissionResult> {
    try {
      // Validate form data
      if (!formData.email || !formData.subject || !formData.message) {
        return {
          success: false,
          error: 'Email, subject, and message are required'
        }
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email)) {
        return {
          success: false,
          error: 'Please enter a valid email address'
        }
      }

      // Get current user (if authenticated)
      const { data: { user } } = await supabase.auth.getUser()

      // Create contact submission record
      const submissionData = {
        email: formData.email,
        category: formData.category,
        subject: formData.subject,
        message: formData.message,
        user_id: user?.id || null,
        created_at: new Date().toISOString(),
        status: 'pending'
      }

      // For now, we'll log this to console and localStorage for development
      // In production, this would save to a support tickets table or send email
      console.log('Contact form submission:', submissionData)

      // Store in localStorage for debugging
      const existingSubmissions = JSON.parse(localStorage.getItem('contact_submissions') || '[]')
      existingSubmissions.push(submissionData)
      localStorage.setItem('contact_submissions', JSON.stringify(existingSubmissions))

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000))

      // TODO: In production, implement one of these approaches:
      // 1. Create a Supabase table for support tickets and insert the record
      // 2. Use a Supabase Edge Function to send emails via SendGrid/Resend
      // 3. Integrate with a support system like Intercom, Zendesk, etc.
      // 4. Send to a webhook endpoint that handles email dispatch

      return { success: true }
    } catch (error) {
      console.error('Contact form submission error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to submit contact form'
      }
    }
  }

  /**
   * Get contact form categories
   */
  getContactCategories() {
    return [
      { value: 'general', label: 'General Question' },
      { value: 'billing', label: 'Billing & Subscription' },
      { value: 'technical', label: 'Technical Issue' },
      { value: 'feature', label: 'Feature Request' },
      { value: 'bug', label: 'Bug Report' }
    ]
  }
}

export const contactService = new ContactService()
export default contactService